import type { StoryblokComponent } from 'storyblok-js-client';
import type { ReactElement, RefObject } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import type { LinkProps } from 'next/dist/client/link';
import type { AbstractCtaProps, CtaRefType } from './cta.data';
import { defaultDisabled, defaultTarget, defaultType } from './cta.data';
import { sbEditable } from '@storyblok/storyblok-editable';
// import { useGlobalStore } from '../../stores/global';
import { stripLocaleFromUrl } from '../url.utils';

export const renderCta = ({
  innerTemplate,
  props,
  elementRef,
  blok,
}: {
  innerTemplate: ReactElement;
  props: AbstractCtaProps;
  elementRef?: RefObject<CtaRefType | null>;
  key?: string;
  blok?: StoryblokComponent<string>;
}): ReactElement => {
  // const globalStore = useGlobalStore();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const router = useRouter();
  const { locale } = router;
  let ctaTemplate;

  const linkTagProps = {
    // ...{
    //   prefetch: true,
    // },
    ...props.link,
    // locale: false,
  };

  const { forceTag, link, ...spreadProps } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let tagProps: any = {
    ...spreadProps,
    type: !link?.href ? props.type || defaultType : null,
    disabled: props.disabled || defaultDisabled,
    'aria-label': props?.['aria-label'] ?? props.label,
    target: link?.href ? props.target || defaultTarget : null,
    className: classNames(spreadProps.className, 'button'),
  };

  if (blok?._uid) {
    tagProps = {
      ...tagProps,
      ...sbEditable(blok),
    };
  }

  // Force target=_blank when it's an external URL.
  if (
    link?.href &&
    typeof link.href === 'string' &&
    (link.href.startsWith('http') || link.href.startsWith('www.')) &&
    !props.target
  ) {
    tagProps.target = '_blank';
  }

  // Force a noooper rel when it's an external-link
  if (tagProps.target === '_blank') {
    tagProps.rel = 'noopener noreferrer';
  }

  if (link?.href) {
    /**
     * Read the comments in the method `stripLocaleFromUrl` for more information.
     */
    linkTagProps.href = stripLocaleFromUrl(link.href, locale);
  }

  // Add missing '/' at start of URL if it's not an 'http/https' url.
  if (
    typeof linkTagProps.href === 'string' &&
    !linkTagProps.href.startsWith('/') &&
    !linkTagProps.href.startsWith('http') &&
    !linkTagProps.href.startsWith('www.') &&
    !linkTagProps.href.includes('mailto:')
  ) {
    linkTagProps.href = `/${linkTagProps.href}`;
  }

  // Add missing 'https://' at start of URL if it's starting with www.
  if (typeof linkTagProps.href === 'string' && linkTagProps.href.startsWith('www.')) {
    linkTagProps.href = `https://${linkTagProps.href}`;
  }

  // Is not an external-link and contains an '@' -> make it an email-link.
  if (
    link &&
    !link.href.startsWith('http') &&
    !link.href.startsWith('www.') &&
    link.href.includes('@') &&
    !link.href.includes('mailto:')
  ) {
    linkTagProps.href = `mailto:${link.href}`;
  }

  if (!link) {
    if (forceTag) {
      delete tagProps.type;
      delete tagProps.target;

      const Tag = forceTag;
      ctaTemplate = (
        <Tag ref={elementRef} {...tagProps}>
          {innerTemplate}
        </Tag>
      );
    } else {
      ctaTemplate = (
        <button ref={elementRef} {...tagProps}>
          {innerTemplate}
        </button>
      );
    }
  } else {
    delete tagProps.type;

    ctaTemplate = (
      <Link {...(linkTagProps as LinkProps)} {...tagProps} ref={elementRef}>
        {innerTemplate}
      </Link>
    );
  }

  return ctaTemplate;
};
